import React, { useEffect, useRef } from 'react';
import './AdSense.css';
import { isReactSnap } from '../StateManagement/isReactSnap';

const SHOW_ADS = process.env.REACT_APP_SHOW_ADS === 'true';
const CLIENT_ID = process.env.REACT_APP_ADSENSE_CLIENT_ID || 'ca-pub-PLACEHOLDER';

/** Supported ad variants with their standard IAB dimensions. */
const AD_VARIANTS = {
  leaderboard: {
    width: '728px',
    height: '90px',
    format: 'horizontal',
    label: 'Advertisement (728×90)',
  },
  rectangle: {
    width: '300px',
    height: '250px',
    format: 'rectangle',
    label: 'Advertisement (300×250)',
  },
  'mobile-banner': {
    width: '320px',
    height: '50px',
    format: 'fixed',
    label: 'Advertisement (320×50)',
  },
};

/**
 * AdSenseAd — renders a real AdSense unit in production or a
 * same-size placeholder banner in development.
 *
 * Props:
 *   variant   – 'leaderboard' | 'rectangle' | 'mobile-banner'
 *   adSlot    – AdSense ad-slot ID string (from env vars)
 *   className – optional extra CSS class
 */
const AdSenseAd = ({ variant = 'rectangle', adSlot, className = '' }) => {
  const insRef = useRef(null);
  const config = AD_VARIANTS[variant] || AD_VARIANTS.rectangle;
  const { width, height, format, label } = config;

  useEffect(() => {
    if (!SHOW_ADS || isReactSnap()) return;

    // Inject the AdSense library script once per page load.
    const scriptId = 'adsense-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${CLIENT_ID}`;
      script.crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error('AdSense push error:', e);
    }
  }, []);

  // Skip rendering entirely during react-snap pre-render.
  if (isReactSnap()) return null;

  if (!SHOW_ADS) {
    return (
      <div
        className={`adsense-placeholder ${className}`}
        style={{ width, height }}
        aria-label="Advertisement placeholder"
      >
        <span className="adsense-placeholder-label">{label}</span>
      </div>
    );
  }

  return (
    <ins
      ref={insRef}
      className={`adsbygoogle ${className}`}
      style={{ display: 'block', width, height }}
      data-ad-client={CLIENT_ID}
      data-ad-slot={adSlot}
      data-ad-format={format}
    />
  );
};

export default AdSenseAd;
